import { CdkScrollable } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSelectionListChange } from '@angular/material/list';
import { PageEvent } from '@angular/material/paginator';

import { Code, trackByCoreResource } from '@mtna/pojo-consumer-ui';

export type RdsFilterSelectionType = 'single' | 'multi';

@Component({
  selector: 'rds-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsFilterComponent {
  /** Required codes to select from */
  @Input() codes: Array<Code>;
  /** Optional ngStyles for code list, defaults to `{ height: 'auto' }`*/
  @Input() codeListStyles: { [key: string]: string } = { height: 'auto' };
  /** Whether to enable pagination */
  @Input() enablePagination: boolean;
  /** Whether to enable text search */
  @Input() enableSearch: boolean;
  /** The length of the total number of codes that are being paginated */
  @Input() length: number;
  /** Whether the codes are loading */
  @Input() loading: boolean;
  /** The zero-based page index of the displayed list of codes */
  @Input() pageIndex: number;
  /** Number of codes to display on a page */
  @Input() pageSize: number;
  /** Search text */
  @Input() searchText: number;
  /** Code(s) the user selected */
  @Input()
  get selectedCodes(): Array<Code> {
    return this._selectedCodes;
  }
  set selectedCodes(codes: Array<Code>) {
    this._selectedCodes = codes;
    this._selectedUris = {};
    this._selectedCodes.forEach((c) => (this._selectedUris[c.uri] = true));
  }
  private _selectedCodes: Array<Code> = [];
  /** Selection type, can be single or multi, defaults to multi */
  @Input() selectionType: RdsFilterSelectionType = 'multi';

  /** Event emitted when the user clears selected codes */
  @Output() clearSelectedCodes = new EventEmitter<null>();
  /** Event emitted when the paginator changes the page size or page index */
  @Output() pageChange = new EventEmitter<PageEvent>();
  /** Text search in codes */
  @Output() search = new EventEmitter<string>();
  /** Event emitted when the user selected a code */
  @Output() selectCode = new EventEmitter<Code>();
  /** Event emitted when the user unselects a code */
  @Output() unselectCode = new EventEmitter<Code>();

  /** the scrollable list when many codes exist. Used for scrollTop on pagination and search */
  @ViewChild(CdkScrollable) scrollContainer: CdkScrollable;

  /** TrackBy function for the *ngFor loop of codes */
  trackByCr = trackByCoreResource;

  /**
   * Object map of selected Code uris.
   *
   * This was necessary because the mat-selection-list was not showing
   * the correct selected option if objects were not the same dispite the compareWith function.
   * As of material v7.3.1
   */
  _selectedUris: { [uri: string]: boolean } = {};

  /**
   * Function used for comparing codes in a mat-selection-list.
   */
  _compareCodes(c1: Code, c2: Code): boolean {
    return c1 && c2 ? c1.uri === c2.uri : c1 === c2;
  }

  /**
   * When a code is selected/unselected from a mat-selection-list,
   * emit the appropriate event.
   *
   * @param change change event that describes which code was selected/unselected
   */
  _handleSelectionListChange(change: MatSelectionListChange) {
    change.option.selected ? this.selectCode.emit(change.option.value) : this.unselectCode.emit(change.option.value);
  }

  /**
   * When a code is selected in a mat-radio group,
   * emit events to unselect the previously selected code
   * and emit an event to select the new code.
   *
   * @param code the code that is selected
   */
  _handleRadioGroupChange(code: Code) {
    if (this.selectedCodes && this.selectedCodes.length) {
      this.unselectCode.emit(this.selectedCodes[0]);
    }
    this.selectCode.emit(code);
  }

  _scrollToTop() {
    if (!!this.scrollContainer) {
      this.scrollContainer.scrollTo({ top: 0 });
    }
  }
}
